import { memo, useEffect, useRef, useState } from 'react';
import { preload } from 'react-dom';

/**
 * Blurred ~20 px thumbnails of the bundled backdrops, inlined as data URIs.
 *
 * There is one per ground the app stands on: `splash-background` behind
 * onboarding (`home_background`) and `ocean-background` behind the journey and
 * the reward screens (`secondary_background`).
 *
 * The point is the *first* paint: a flat navy screen that a photograph lands on
 * a second later reads as a flash, while the same photograph resolving out of
 * its own colours and shapes reads as a reveal. Roughly 470 bytes each, which
 * is cheaper than the round trip it saves.
 *
 * Keyed by the public path, because `site_settings` may point the backdrop at a
 * Prismic image instead — in which case there is no thumbnail to inline and the
 * ground falls back to brand navy. Regenerate with a 20 px LANCZOS resize plus
 * a 0.6 px blur at JPEG q42.
 */
const GROUND_BY_SRC: Record<string, string> = {
  '/images/splash-background.webp':
    'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDABMNDhEODBMRDxEVFBMXHTAfHRoaHToqLCMwRT1JR0Q9Q0FMVm1dTFFoUkFDX4JgaHF1e3x7SlyGkIV3j214e3b/2wBDARQVFR0ZHTgfHzh2T0NPdnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnb/wAARCAANABQDASIAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAAAwQA/8QAHRAAAgICAwEAAAAAAAAAAAAAAQIAEQMhBBITMf/EABYBAQEBAAAAAAAAAAAAAAAAAAECA//EABoRAAMAAwEAAAAAAAAAAAAAAAABAhITMUH/2gAMAwEAAhEDEQA/ALzzm9mQLQYCtWRF7Y+pUlrOyaqQY69C9br7GOQkQmmui5XghOInS6mkjue0002EYH//2Q==',
  '/images/ocean-background.webp':
    'data:image/jpeg;base64,/9j/2wBDABMNDhEODBMRDxEVFBMXHTAfHRoaHToqLCMwRT1JR0Q9Q0FMVm1dTFFoUkFDX4JgaHF1e3x7SlyGkIV3j214e3b/2wBDARQVFR0ZHTgfHzh2T0NPdnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnb/wAARCAALABQDASIAAhEBAxEB/8QAFwABAQEBAAAAAAAAAAAAAAAAAAQBBf/EABoQAAICAwAAAAAAAAAAAAAAAAABAgQUMUH/xAAWAQEBAQAAAAAAAAAAAAAAAAAABAX/xAAZEQACAwEAAAAAAAAAAAAAAAAAAhITUQH/2gAMAwEAAhEDEQA/AOar8kYr0k9siBuUphHPpdnS62CEClME2P/Z',
};

/**
 * The onboarding ground: brand colour, then the inlined thumbnail, then the
 * photograph cross-fading over both, then the directional scrim.
 *
 * Nothing here is in the layout — the whole stack is absolutely positioned
 * behind the content — so no part of the sequence can shift anything, and the
 * scrim is painted from the first frame so the tone never changes as the photo
 * arrives.
 *
 * The photograph is revealed only once `decode()` has resolved, so it paints in
 * one go rather than progressively. `preload` asks for it at head time, which
 * is a beat earlier than the `<img>` alone would be discovered on a cold
 * client navigation.
 *
 * The whole stack is mounted once for the flow by `routes/onboarding-layout`
 * and survives every step, so the reveal below runs on arrival and never
 * again. `memo` keeps it out of the layout's re-renders, and `DECODED` covers
 * the mounts the layout cannot: reaching the profile screen from the journey's
 * settings menu, or starting over, mounts a fresh stage, and a photograph this
 * session has already decoded should be on screen from that mount's first
 * frame rather than fading up out of its thumbnail a second time.
 */
const DECODED = new Set<string>();

export const OnboardingBackdrop = memo(function OnboardingBackdrop({ src }: { src: string }) {
  // Never `true` on the server or on the first client render — nothing has
  // decoded in either — so the reveal cannot cause a hydration mismatch.
  const [ready, setReady] = useState(() => DECODED.has(src));
  const image = useRef<HTMLImageElement>(null);

  preload(src, { as: 'image', fetchPriority: 'high' });

  useEffect(() => {
    const element = image.current;

    if (element === null) {
      return;
    }

    let cancelled = false;

    // `decode()` resolves once the frame is ready to paint in one go, and
    // rejects if the file is broken or the element is replaced — in which case
    // the photo is revealed anyway, so a failure can never leave the screen
    // stuck on the thumbnail.
    const reveal = () => {
      DECODED.add(src);

      if (!cancelled) {
        setReady(true);
      }
    };

    void element.decode().then(reveal, reveal);

    return () => {
      cancelled = true;
    };
  }, [src]);

  const ground = GROUND_BY_SRC[src];

  return (
    <div aria-hidden className="absolute inset-0 -z-10 overflow-hidden">
      <div
        className="bp-ground absolute inset-0"
        style={ground === undefined ? undefined : ({ '--bp-ground-image': `url(${ground})` } as React.CSSProperties)}
      />
      {/*
        A plain <img> rather than a CSS background so the browser can pick it up
        as an LCP candidate and prioritise it. It is hidden until the effect
        below has decoded it, so it never paints half-drawn and the decode
        never blocks the main thread.
      */}
      <img
        ref={image}
        src={src}
        alt=""
        fetchPriority="high"
        decoding="async"
        data-ready={ready ? 'true' : undefined}
        className="bp-photo absolute inset-0 size-full object-cover"
      />
      <div className="bp-scrim absolute inset-0" />
    </div>
  );
});
